import { useState } from "react";
import {
  stages,
  venueParts,
  venueTime,
  type Command,
  type Config,
  type Match,
  type Tournament,
} from "@spikeeasy/tournament";
import { TeamsView } from "./TournamentViews";
import { TieResolution } from "./TieResolution";
import { Dialog, Field, Notice, teamLabel, stageLabels, teamName } from "./ui";

type Send = (command: Command) => Promise<void>;
export function ManageMatch({
  t,
  match,
  send,
  close,
  busy,
}: {
  t: Tournament;
  match: Match;
  send: Send;
  close: () => void;
  busy: boolean;
}) {
  const [courtId, setCourt] = useState(match.courtId ?? t.courts[0]!.id);
  const [mode, setMode] = useState(match.refereeMode);
  const [ref, setRef] = useState(match.refereeId ?? "");
  const [release, setRelease] = useState(match.needsRelease);
  const parts = match.notBefore && venueParts(match.notBefore, t.timezone);
  const [notBefore, setNotBefore] = useState(
    parts ? `${parts.date}T${parts.time}` : "",
  );
  const [error, setError] = useState("");
  const [retireWinner, setRetireWinner] = useState("");
  const [reason, setReason] = useState("");
  const run = async (command: Command) => {
    try {
      await send(command);
      close();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Dialog title={`${match.id} · Match settings`} onClose={close}>
      <div className="stack">
        <p>
          {teamName(t, match.teams[0])} vs {teamName(t, match.teams[1])}
        </p>
        {error && <Notice error>{error}</Notice>}
        <Field label="Court">
          <select value={courtId} onChange={(e) => setCourt(e.target.value)}>
            {t.courts.map((court) => (
              <option key={court.id} value={court.id}>
                {court.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Refereeing">
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as Match["refereeMode"])}
          >
            <option value="auto">Assign automatically</option>
            <option value="team">Choose a referee team</option>
            <option value="self">Self-ref</option>
          </select>
        </Field>
        {mode === "team" && (
          <Field label="Referee team">
            <select value={ref} onChange={(e) => setRef(e.target.value)}>
              <option value="">Choose a team</option>
              {t.teams
                .filter(
                  (team) =>
                    !match.teams.includes(team.id) &&
                    (!match.poolId ||
                      t.pools
                        .find((p) => p.id === match.poolId)
                        ?.teamIds.includes(team.id)),
                )
                .map((team) => (
                  <option key={team.id} value={team.id}>
                    {teamLabel(team)}
                  </option>
                ))}
            </select>
          </Field>
        )}
        <Field label={`Not before (${t.timezone})`}>
          <input
            type="datetime-local"
            value={notBefore}
            onChange={(e) => setNotBefore(e.target.value)}
          />
        </Field>
        <label className="check">
          <input
            type="checkbox"
            checked={release}
            onChange={(e) => setRelease(e.target.checked)}
          />
          Require organizer release
        </label>
        <button
          className="primary"
          disabled={busy || (mode === "team" && !ref)}
          onClick={() =>
            run({
              type: "assign",
              matchId: match.id,
              courtId,
              refereeMode: mode,
              refereeId: ref || undefined,
              needsRelease: release,
              notBefore: notBefore
                ? venueTime(
                    notBefore.split("T")[0]!,
                    notBefore.split("T")[1]!,
                    t.timezone,
                  )
                : null,
            })
          }
        >
          Save assignment
        </button>
        {match.status === "ready" && match.needsRelease && !match.released && (
          <button
            className="secondary"
            disabled={busy}
            onClick={() => run({ type: "release", matchId: match.id })}
          >
            Release match to play
          </button>
        )}
        {match.status === "live" && (
          <details>
            <summary>Record a retirement</summary>
            <div className="stack">
              <Field label="Team advancing">
                <select
                  value={retireWinner}
                  onChange={(e) => setRetireWinner(e.target.value)}
                >
                  <option value="">Choose the winner</option>
                  {match.teams.map((id) => (
                    <option key={id} value={id!}>
                      {teamName(t, id)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Reason">
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </Field>
              <button
                className="secondary"
                disabled={!retireWinner || reason.trim().length < 3 || busy}
                onClick={() =>
                  run({
                    type: "retire",
                    matchId: match.id,
                    revision: match.revision,
                    winnerId: retireWinner,
                    reason,
                  })
                }
              >
                Confirm retirement
              </button>
            </div>
          </details>
        )}
      </div>
    </Dialog>
  );
}

export function Organizer({
  t,
  send,
  busy,
  edit,
}: {
  t: Tournament;
  send: Send;
  busy: boolean;
  edit: () => void;
}) {
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [withdrawing, setWithdrawing] = useState("");
  const [replacement, setReplacement] = useState("");
  const [reason, setReason] = useState("");
  const [manual, setManual] = useState("");
  const [configOpen, setConfigOpen] = useState(false);
  const run = async (command: Command) => {
    setError("");
    try {
      await send(command);
      setMessage("Saved.");
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const lastWin = t.matches.findLast(
    (m) => m.winnerId === withdrawing && m.resolution !== "bye",
  );
  return (
    <div className="stack">
      <h2>Organizer desk</h2>
      {error && <Notice error>{error}</Notice>}
      {message && <Notice>{message}</Notice>}
      {t.status === "draft" && (
        <section className="editor stack">
          <h3>Ready for the beach?</h3>
          <p>
            Publishing makes the draw public and freezes the starting seeds and
            points table. Both playing teams and the referee can score each
            game. Semifinals and finals wait for organizer release.
          </p>
          <div className="actions">
            <button
              className="primary"
              disabled={busy}
              onClick={() => run({ type: "publish" })}
            >
              Publish tournament
            </button>
            <button className="text-button" onClick={edit}>
              Edit draft
            </button>
          </div>
        </section>
      )}
      <details>
        <summary>Teams & access codes</summary>
        <TeamsView t={t} actor={{ role: "admin" }} />
      </details>
      {t.status === "running" && (
        <>
          <button className="secondary" onClick={() => setConfigOpen(true)}>
            Stage scoring & release settings
          </button>
          {t.pools.length > 0 && (
            <details>
              <summary>Pool court assignments</summary>
              <div className="stack">
                {t.pools.map((pool) => (
                  <Field key={pool.id} label={`Pool ${pool.id}`}>
                    <select
                      disabled={busy}
                      value={pool.courtId}
                      onChange={(e) =>
                        run({
                          type: "pool_court",
                          poolId: pool.id,
                          courtId: e.target.value,
                        })
                      }
                    >
                      {t.courts.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                ))}
              </div>
            </details>
          )}
          {!t.playoffGenerated && (
            <TieResolution t={t} send={send} busy={busy} />
          )}
          <details>
            <summary>Withdraw a team</summary>
            <div className="stack">
              <p>
                For a live match, record a retirement in its match settings.
                Between elimination games, reinstate the last opponent the
                withdrawing team beat, if available.
              </p>
              <Field label="Withdrawing team">
                <select
                  value={withdrawing}
                  onChange={(e) => {
                    setWithdrawing(e.target.value);
                    setReplacement(
                      t.matches.findLast(
                        (m) =>
                          m.winnerId === e.target.value &&
                          m.resolution !== "bye",
                      )?.loserId ?? "",
                    );
                  }}
                >
                  <option value="">Choose a team</option>
                  {t.teams
                    .filter((team) => !team.withdrawn)
                    .map((team) => (
                      <option key={team.id} value={team.id}>
                        {teamLabel(team)}
                      </option>
                    ))}
                </select>
              </Field>
              {lastWin && (
                <p>Last beaten opponent: {teamName(t, lastWin.loserId)}</p>
              )}
              <Field label="Available replacement for an elimination game">
                <select
                  value={replacement}
                  onChange={(e) => setReplacement(e.target.value)}
                >
                  <option value="">No replacement available · walkover</option>
                  {t.teams
                    .filter(
                      (team) => !team.withdrawn && team.id !== withdrawing,
                    )
                    .map((team) => (
                      <option key={team.id} value={team.id}>
                        {teamLabel(team)}
                      </option>
                    ))}
                </select>
              </Field>
              <Field label="Reason / availability notes">
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                />
              </Field>
              <button
                className="secondary"
                disabled={!withdrawing || reason.trim().length < 3 || busy}
                onClick={() =>
                  run({
                    type: "withdraw",
                    teamId: withdrawing,
                    replacementId: replacement || undefined,
                    reason,
                  })
                }
              >
                Confirm withdrawal & replacement
              </button>
            </div>
          </details>
          {t.config.mode === "swiss" && !t.playoffGenerated && (
            <details>
              <summary>Manual Swiss pairing</summary>
              <div className="stack">
                <p>
                  Use if an automatic draw without rematches cannot be found.
                  Enter one pairing per line as team ID,team ID. Leave the
                  second ID blank for the single permitted bye.
                </p>
                <p className="muted">
                  {t.teams
                    .map((team) => `${team.id}: ${teamLabel(team)}`)
                    .join("; ")}
                </p>
                <Field label="Next round pairs">
                  <textarea
                    rows={5}
                    value={manual}
                    onChange={(e) => setManual(e.target.value)}
                  />
                </Field>
                <button
                  className="secondary"
                  disabled={busy}
                  onClick={() =>
                    run({
                      type: "manual_swiss",
                      pairs: manual
                        .trim()
                        .split("\n")
                        .map((row) => {
                          const [a, b] = row.split(",").map((v) => v.trim());
                          return [a ?? "", b || null];
                        }),
                    })
                  }
                >
                  Confirm next round
                </button>
              </div>
            </details>
          )}
        </>
      )}
      {configOpen && (
        <ConfigDialog
          t={t}
          busy={busy}
          send={send}
          close={() => setConfigOpen(false)}
        />
      )}
    </div>
  );
}

function ConfigDialog({
  t,
  send,
  close,
  busy,
}: {
  t: Tournament;
  send: Send;
  close: () => void;
  busy: boolean;
}) {
  const [config, setConfig] = useState<Config>(structuredClone(t.config));
  const [error, setError] = useState("");
  return (
    <Dialog title="Stage rules" onClose={close}>
      <div className="stack">
        <p>
          Changes apply to games that have not started. Individual match
          settings remain available in the draw.
        </p>
        {error && <Notice error>{error}</Notice>}
        {!t.playoffGenerated && (
          <Field
            label="Total playoff qualifiers"
            hint={
              config.mode === "swiss"
                ? "Use zero to finish with Swiss standings."
                : "At least two teams must qualify."
            }
          >
            <input
              type="number"
              min={config.mode === "swiss" ? 0 : 2}
              max={t.teams.filter((team) => !team.withdrawn).length}
              value={config.qualifiers}
              onChange={(e) =>
                setConfig({ ...config, qualifiers: Number(e.target.value) })
              }
            />
          </Field>
        )}
        {config.mode === "swiss" && !t.playoffGenerated && (
          <Field label="Total Swiss rounds">
            <input
              type="number"
              min={Math.max(1, ...t.matches.map((m) => m.round))}
              max={20}
              value={config.swissRounds}
              onChange={(e) =>
                setConfig({ ...config, swissRounds: Number(e.target.value) })
              }
            />
          </Field>
        )}
        {config.mode === "king" && !t.playoffGenerated && (
          <Field label="Total heat rounds">
            <input
              type="number"
              min={Math.max(1, ...t.heats.map((h) => h.round))}
              max={20}
              value={config.heatCount}
              onChange={(e) =>
                setConfig({ ...config, heatCount: Number(e.target.value) })
              }
            />
          </Field>
        )}
        {stages
          .filter(
            (stage) =>
              t.matches.some((m) => m.stage === stage) ||
              ["playoff", "semifinal", "final"].includes(stage) ||
              (stage === "bronze" && config.bronze),
          )
          .map((stage) => {
            const rule = config.stageScoring[stage] ?? config.scoring;
            const value = config.notBefore[stage];
            const parts = value && venueParts(value, t.timezone);
            return (
              <details key={stage}>
                <summary>{stageLabels[stage]}</summary>
                <div className="stack">
                  <div className="form-row">
                    <Field label="Target points">
                      <input
                        type="number"
                        min={1}
                        max={99}
                        value={rule.target}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            stageScoring: {
                              ...config.stageScoring,
                              [stage]: {
                                ...rule,
                                target: Number(e.target.value),
                              },
                            },
                          })
                        }
                      />
                    </Field>
                    <Field label="Sets to win">
                      <input
                        type="number"
                        min={1}
                        max={3}
                        value={rule.setsToWin}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            stageScoring: {
                              ...config.stageScoring,
                              [stage]: {
                                ...rule,
                                setsToWin: Number(e.target.value),
                              },
                            },
                          })
                        }
                      />
                    </Field>
                  </div>
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={rule.winByTwo}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          stageScoring: {
                            ...config.stageScoring,
                            [stage]: { ...rule, winByTwo: e.target.checked },
                          },
                        })
                      }
                    />
                    Win by two
                  </label>
                  <Field label="Hard cap">
                    <input
                      type="number"
                      min={rule.target}
                      value={rule.cap ?? ""}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          stageScoring: {
                            ...config.stageScoring,
                            [stage]: {
                              ...rule,
                              cap: e.target.value
                                ? Number(e.target.value)
                                : null,
                            },
                          },
                        })
                      }
                    />
                  </Field>
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={config.releaseStages.includes(stage)}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          releaseStages: e.target.checked
                            ? [...config.releaseStages, stage]
                            : config.releaseStages.filter((s) => s !== stage),
                        })
                      }
                    />
                    Organizer release
                  </label>
                  <Field label={`Not before (${t.timezone})`}>
                    <input
                      type="datetime-local"
                      value={parts ? `${parts.date}T${parts.time}` : ""}
                      onChange={(e) => {
                        const next = { ...config.notBefore };
                        if (e.target.value) {
                          const [date, time] = e.target.value.split("T");
                          next[stage] = venueTime(date!, time!, t.timezone);
                        } else delete next[stage];
                        setConfig({ ...config, notBefore: next });
                      }}
                    />
                  </Field>
                </div>
              </details>
            );
          })}
        <button
          className="primary"
          disabled={busy}
          onClick={async () => {
            try {
              await send({ type: "settings", config });
              close();
            } catch (e) {
              setError((e as Error).message);
            }
          }}
        >
          Save stage rules
        </button>
      </div>
    </Dialog>
  );
}
